/**
 * instructorAiProfile.js
 * Utilities for instructor AI profile data — normalisation, clause building,
 * and derived-signal helpers.
 *
 * Firestore path: instructorAiProfiles/{instructorId}
 */

export const COACHING_TONES = ['encouraging', 'direct', 'technical', 'nurturing', 'challenging'];
export const VERBOSITY_OPTIONS = ['concise', 'moderate', 'detailed'];
export const DRILL_STYLES = ['isolation', 'game-based', 'flow', 'pressure', 'mirror'];

const SCHEMA_VERSION = 1;
const DERIVED_STALE_DAYS = 7;

/**
 * Return a blank profile with safe defaults.
 * Used when a doc doesn't yet exist in Firestore.
 */
export function emptyInstructorAiProfile(instructorId, orgId) {
  return {
    instructorId,
    orgId,
    overrides: {
      tone: null,
      verbosity: 'moderate',
      drillStyle: null,
      usesAnalogies: false,
    },
    derived: {
      toneFrequency: {},
      preferredDrills: [],
      lessonStructure: [],
      avgDraftEditDistance: null,
      lastDerivedAt: null,
    },
    phrasesBank: [],
    lastUpdated: null,
    schemaVersion: SCHEMA_VERSION,
  };
}

/**
 * Normalise a raw Firestore doc into a safe profile shape.
 * Handles missing fields gracefully so callers don't need to null-check.
 */
export function normalizeInstructorAiProfile(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const overrides = raw.overrides || {};
  const derived = raw.derived || {};

  return {
    instructorId: raw.instructorId ?? null,
    orgId: raw.orgId ?? null,
    overrides: {
      tone: COACHING_TONES.includes(overrides.tone) ? overrides.tone : null,
      verbosity: VERBOSITY_OPTIONS.includes(overrides.verbosity)
        ? overrides.verbosity
        : 'moderate',
      drillStyle: DRILL_STYLES.includes(overrides.drillStyle) ? overrides.drillStyle : null,
      usesAnalogies: typeof overrides.usesAnalogies === 'boolean' ? overrides.usesAnalogies : false,
    },
    derived: {
      toneFrequency:
        derived.toneFrequency && typeof derived.toneFrequency === 'object'
          ? derived.toneFrequency
          : {},
      preferredDrills: Array.isArray(derived.preferredDrills) ? derived.preferredDrills : [],
      lessonStructure: Array.isArray(derived.lessonStructure) ? derived.lessonStructure : [],
      avgDraftEditDistance:
        typeof derived.avgDraftEditDistance === 'number' ? derived.avgDraftEditDistance : null,
      lastDerivedAt: derived.lastDerivedAt ?? null,
    },
    phrasesBank: Array.isArray(raw.phrasesBank) ? raw.phrasesBank : [],
    lastUpdated: raw.lastUpdated ?? null,
    schemaVersion: raw.schemaVersion ?? SCHEMA_VERSION,
  };
}

/**
 * Given a toneFrequency map like { encouraging: 12, direct: 5 },
 * return the tone with the highest count. Returns null if the map is empty.
 */
export function computeDominantTone(toneFrequency) {
  if (!toneFrequency || typeof toneFrequency !== 'object') return null;
  const entries = Object.entries(toneFrequency).filter(
    ([tone, count]) => COACHING_TONES.includes(tone) && typeof count === 'number' && count > 0
  );
  if (entries.length === 0) return null;
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][0];
}

/**
 * Resolve the effective tone for a generation call.
 * Explicit override wins; falls back to computed dominant tone; then null.
 */
function resolveEffectiveTone(profile) {
  if (profile.overrides?.tone) return profile.overrides.tone;
  return computeDominantTone(profile.derived?.toneFrequency);
}

/**
 * Build a plain-text string to append to AI task instructions.
 * Returns an empty string when the profile adds no useful signal.
 *
 * The clause is appended AFTER instructorContext so quick-input overrides win.
 */
export function buildInstructorProfileClause(profile) {
  if (!profile) return '';

  const lines = [];

  const tone = resolveEffectiveTone(profile);
  if (tone) lines.push(`Instructor coaching tone: ${tone}.`);

  const verbosity = profile.overrides?.verbosity;
  if (verbosity && verbosity !== 'moderate') {
    lines.push(`Draft verbosity preference: ${verbosity}.`);
  }

  const drillStyle = profile.overrides?.drillStyle;
  if (drillStyle) lines.push(`Preferred drill style: ${drillStyle}.`);

  if (profile.overrides?.usesAnalogies) {
    lines.push('This instructor frequently uses analogies — include one where natural.');
  }

  const preferredDrills = profile.derived?.preferredDrills;
  if (Array.isArray(preferredDrills) && preferredDrills.length > 0) {
    lines.push(`Drills this instructor commonly assigns: ${preferredDrills.join(', ')}.`);
  }

  const lessonStructure = profile.derived?.lessonStructure;
  if (Array.isArray(lessonStructure) && lessonStructure.length > 0) {
    lines.push(`Typical lesson structure this instructor follows: ${lessonStructure.join(' → ')}.`);
  }

  const phrases = profile.phrasesBank;
  if (Array.isArray(phrases) && phrases.length > 0) {
    lines.push(`Phrases this instructor commonly uses: "${phrases.slice(0, 3).join('", "')}".`);
  }

  if (lines.length === 0) return '';

  return `\n\nInstructor style preferences:\n${lines.join('\n')}`;
}

/**
 * Return true if the derived layer is stale and should be recomputed.
 */
export function isDerivedStale(profile) {
  if (!profile?.derived?.lastDerivedAt) return true;
  const last = profile.derived.lastDerivedAt.toDate
    ? profile.derived.lastDerivedAt.toDate()
    : new Date(profile.derived.lastDerivedAt);
  const ageMs = Date.now() - last.getTime();
  return ageMs > DERIVED_STALE_DAYS * 24 * 60 * 60 * 1000;
}
